<template>
  <div class="recipients-list">
    <recipient-modal
      v-if="showRecipientModal"
      @close="closeRecipientModal()"
    />

    <manual-transfer-modal
      v-if="showManualTransferModal"
      @close="closeManualTransferModal()"
    />
    <div class="form-group recipient-title">
      <div>
        <strong>Recebedores:</strong>
      </div>
      <button
        class="btn btn-primary"
        @click="createRecipient()"
        title="Novo recebedor"
        v-permissions:recipient.create
      >
        <i class="fas fa-plus"></i>
        Novo recebedor
      </button>
    </div>
    <table class="table table-striped table-bordered">
      <thead>
        <tr>
          <th class="table-centralized">
            ID
          </th>
          <th class="table-centralized">
            Nome/CNPJ
          </th>
          <th class="table-centralized">
            Saldo
          </th>
          <th class="table-centralized">
            Ativo
          </th>
          <th class="table-centralized">
            Transferência
          </th>
          <th class="table-centralized">
            BRG associada
            <span
              class="icon--micro glyphicon glyphicon-info-sign"
              data-toggle="tooltip"
              title="Indica que o recebedor é utilizado por uma Regra de negócio (BRG) para realizar transferências"
            >
            </span>
          </th>
          <th class="table-centralized">
            Conta bancária
          </th>
          <th>
            <dropdown-filter-list
              class="table-centralized"
              text-displayed="Filtro de recebedores ativos"
              :reference-id="`recipients-${this.institution.id}`"
              :items-filter="itemsFilter"
              :pre-selected-items="enabledItemsFilter"
              @items-changed="filterChanged"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="this.recipients.length === 0">
          <td colspan="8" class="table-centralized">
            Não existem recebedores para está instituição.
          </td>
        </tr>
        <tr v-else-if="this.filteredRecipients.length === 0">
          <td colspan="8" class="table-centralized">
            Nada a exibir considerando o filtro atual.
          </td>
        </tr>
        <tr
          v-for="recipient in this.filteredRecipients"
        >
          <td class="table-centralized">
            #{{ recipient.id }}
          </td>
          <td class="table-centralized">
            {{ recipient.name }}
            <br>
            {{ recipient.document_formatted }}
            <br>
            Empresa <strong>{{ recipient.government_status }} </strong> na Receita Federal
          </td>
          <td class="table-centralized">
            <strong>Atual: </strong>
            {{ (recipient.psp_data.pagarme_available_value / 100.0) || 0 | formatCurrency }}
            <br>
            <strong>Futuro: </strong>
            {{ (recipient.psp_data.pagarme_waiting_funds_value / 100.0) || 0 | formatCurrency }}
          </td>
          <td class="table-centralized">
            {{ recipient.enabled | formatBoolean }}
          </td>
          <td class="table-centralized">
            {{ translateAllowTransfer(recipient) }}
          </td>
          <td class="table-centralized">
            {{ recipient.has_enabled_brg | formatBoolean }}
          </td>
          <td class="table-centralized">
            <div v-if="recipient.bank_account">
              <strong>Banco: </strong> {{ recipient.bank_account.bank_code }}
              <br>
              <strong>Tipo: </strong> {{ recipient.bank_account.translated_bank_account_type }}
              <br>
              <strong>Agência: </strong> {{ agency(recipient.bank_account) }}
              <br>
              <strong>Conta: </strong> {{ account(recipient.bank_account) }}
            </div>
            <div v-else>
              Não possui
            </div>
          </td>
          <td class="table-centralized">
            <div class="action-buttons">
              <button
                class="btn btn-primary action-buttions--green"
                @click="createManualTransfer(recipient)"
                :disabled="!recipient.enabled"
                title="Transferência manual"
                v-permissions:recipient.update_bank_account
              >
                <i class="fas fa-dollar-sign"></i>
              </button>
              <button
                class="btn btn-primary action-buttons-margin"
                @click="updateRecipient(recipient)"
                :disabled="!recipient.enabled"
                title="Editar recebedor"
                v-permissions:recipient.update_bank_account
              >
                <i class="fas fa-edit"></i>
              </button>
              <button
                class="btn btn-danger action-buttons-margin"
                @click="disableTransfers(recipient)"
                title="Desabilitar transferências"
                v-if="recipient.allow_transfer"
                v-permissions:recipient.toggle_allow_transfer
              >
                <i class="fas fa-pause"></i>
              </button>
              <button
                class="btn btn-primary action-buttons-margin"
                @click="enableTransfers(recipient)"
                title="Habilitar transferências"
                v-permissions:recipient.toggle_allow_transfer
                v-else
              >
                <i class="fas fa-play"></i>
              </button>
              <button
                class="btn btn-danger action-buttons-margin"
                @click="disableRecipient(recipient)"
                :disabled="disableButton(recipient)"
                title="Desativar recebedor"
                v-permissions:recipient.disable
              >
                <i class="fas fa-ban"></i>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import DropdownFilterList from 'components/DropdownFilterList';
import Permissions from 'mixins/Permissions';
import RecipientModal from 'pages/Institutions/InstitutionView/modals/RecipientModal';
import ManualTransferModal from 'pages/Institutions/InstitutionView/modals/ManualTransferModal';

export default {
  mixins: [Permissions],

  components: {
    DropdownFilterList,
    RecipientModal,
    ManualTransferModal,
  },

  computed: {
    ...mapState([
      'institution',
      'recipients',
    ]),

    filteredRecipients() {
      return this.recipients
        .filter(({ enabled }) => this.enabledItemsFilter.includes(enabled));
    },
  },

  data() {
    return {
      enabledItemsFilter: [true],
      itemsFilter: [
        { value: true, text: 'Ativos' },
        { value: false, text: 'Desativados' },
      ],
      showRecipientModal: false,
      showManualTransferModal: false,
    };
  },

  methods: {
    ...mapActions([
      'loadRecipients',
    ]),

    ...mapMutations([
      'setSelectedRecipient',
    ]),

    account(bankAccount) {
      if (bankAccount.digit) return `${bankAccount.number}-${bankAccount.digit}`;

      return bankAccount.number;
    },

    agency(bankAccount) {
      if (bankAccount.agency_digit) return `${bankAccount.agency_code}-${bankAccount.agency_digit}`;

      return bankAccount.agency_code;
    },

    closeRecipientModal() {
      this.loadRecipients(this.institution.id).then(() => {
        this.showRecipientModal = false;
        this.setSelectedRecipient(null);
      });
    },

    closeManualTransferModal() {
      this.loadRecipients(this.institution.id).then(() => {
        this.showManualTransferModal = false;
      });
    },

    createRecipient() {
      this.showRecipientModal = true;
      this.setRecipientAndAction({ institution_id: this.institution.id }, 'createRecipient');
    },

    disableButton(recipient) {
      return !recipient.enabled || recipient.has_enabled_brg;
    },

    disableRecipient(recipient) {
      this.showRecipientModal = true;
      this.setRecipientAndAction(recipient, 'disableRecipient');
    },

    disableTransfers(recipient) {
      this.showRecipientModal = true;
      this.setRecipientAndAction(recipient, 'disableTransfers');
    },

    enableTransfers(recipient) {
      this.showRecipientModal = true;
      this.setRecipientAndAction(recipient, 'enableTransfers');
    },

    filterChanged(items) {
      this.enabledItemsFilter = items;
    },

    initializeTooltips() {
      $(() => {
        $('[data-toggle="tooltip"]').tooltip();
      });
    },

    setRecipientAndAction(recipient, modalAction) {
      this.setSelectedRecipient(Object.assign(recipient, { modalAction }));
    },

    translateAllowTransfer(recipient) {
      return recipient.allow_transfer ? 'Habilitada' : 'Desabilitada';
    },

    updateRecipient(recipient) {
      this.showRecipientModal = true;
      this.setRecipientAndAction(recipient, 'updateRecipient');
    },

    createManualTransfer(recipient) {
      this.showManualTransferModal = true;
      this.setRecipientAndAction(recipient, 'updateRecipient');
    },
  },

  mounted() {
    this.initializeTooltips();
  },
};
</script>

<style scoped lang="scss">
.action-buttons-margin {
  margin-left: 5px;
}

.recipient-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.action-buttions--green {
  background-color: green;

  &:hover {
    background-color: green;
    filter: brightness(85%);
  }
}
</style>
